import { subDays } from "date-fns";

export interface CustomerMetricInput {
  contact_id?: string | null;
  created_at: string;
  last_activity_at: string | null;
  total_bookings: number;
  booking_created_at?: string[];
  archived_at?: string | null;
}

export interface CustomerMetrics {
  monthlyActive: number;
  inactive: number;
  newThisMonth: number;
  repeatRate: number;
}

export interface CustomerActivityEvent {
  contact_id: string;
  event_type: string;
  occurred_at: string;
}

export interface MetricTrend {
  points: number[];
  growth: number;
}

export interface CustomerMetricTrends {
  monthlyActive: MetricTrend;
  inactive: MetricTrend;
  newThisMonth: MetricTrend;
  repeatRate: MetricTrend;
}

const validTimestamp = (value: string | null | undefined) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

/** Customer-health totals for current, non-archived contacts. */
export function calculateCustomerMetrics(
  customers: CustomerMetricInput[],
  now = new Date(),
): CustomerMetrics {
  const nowTimestamp = now.getTime();
  const activeCutoff = subDays(now, 30).getTime();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  let bookedCustomers = 0;
  let repeatCustomers = 0;

  const metrics = customers.reduce<Omit<CustomerMetrics, "repeatRate">>((totals, customer) => {
    if (customer.archived_at) return totals;

    const lastActivity = validTimestamp(customer.last_activity_at);
    const isMonthlyActive = lastActivity !== null
      && lastActivity >= activeCutoff
      && lastActivity <= nowTimestamp;

    if (isMonthlyActive) totals.monthlyActive += 1;
    else totals.inactive += 1;

    const createdAt = validTimestamp(customer.created_at);
    if (createdAt !== null && createdAt <= nowTimestamp) {
      const createdDate = new Date(createdAt);
      if (createdDate.getFullYear() === currentYear && createdDate.getMonth() === currentMonth) {
        totals.newThisMonth += 1;
      }
    }

    if (customer.total_bookings >= 1) bookedCustomers += 1;
    if (customer.total_bookings >= 2) repeatCustomers += 1;
    return totals;
  }, {
    monthlyActive: 0,
    inactive: 0,
    newThisMonth: 0,
  });

  return {
    ...metrics,
    repeatRate: bookedCustomers === 0 ? 0 : (repeatCustomers / bookedCustomers) * 100,
  };
}

const growthBetweenLastPoints = (points: number[]) => {
  const current = points.at(-1) ?? 0;
  const previous = points.at(-2) ?? 0;
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / previous) * 100;
};

/** Eight weekly snapshots ending now, used by the compact metric sparklines. */
export function calculateCustomerMetricTrends(
  customers: CustomerMetricInput[],
  events: CustomerActivityEvent[],
  now = new Date(),
): CustomerMetricTrends {
  const currentCustomers = customers.filter((customer) => !customer.archived_at);
  const activityByContact = new Map<string, number[]>();
  const bookingEventsByContact = new Map<string, number[]>();

  for (const event of events) {
    const timestamp = validTimestamp(event.occurred_at);
    if (timestamp === null) continue;
    const activity = activityByContact.get(event.contact_id) ?? [];
    activity.push(timestamp);
    activityByContact.set(event.contact_id, activity);
    if (event.event_type === "BookingCreated") {
      const bookings = bookingEventsByContact.get(event.contact_id) ?? [];
      bookings.push(timestamp);
      bookingEventsByContact.set(event.contact_id, bookings);
    }
  }

  const snapshots = Array.from({ length: 8 }, (_, index) =>
    subDays(now, (7 - index) * 7),
  );
  const monthlyActive: number[] = [];
  const inactive: number[] = [];
  const newThisMonth: number[] = [];
  const repeatRate: number[] = [];

  snapshots.forEach((snapshot, snapshotIndex) => {
    const snapshotTimestamp = snapshot.getTime();
    const activeCutoff = subDays(snapshot, 30).getTime();
    let activeCount = 0;
    let eligibleCount = 0;
    let newCount = 0;
    let bookedCount = 0;
    let repeatCount = 0;

    for (const customer of currentCustomers) {
      const createdAt = validTimestamp(customer.created_at);
      if (createdAt === null || createdAt > snapshotTimestamp) continue;
      eligibleCount += 1;

      const activityDates = customer.contact_id
        ? [...(activityByContact.get(customer.contact_id) ?? [])]
        : [];
      const lastActivity = validTimestamp(customer.last_activity_at);
      if (lastActivity !== null) activityDates.push(lastActivity);
      if (activityDates.some((timestamp) =>
        timestamp >= activeCutoff && timestamp <= snapshotTimestamp
      )) {
        activeCount += 1;
      }

      const createdDate = new Date(createdAt);
      if (
        createdDate.getFullYear() === snapshot.getFullYear()
        && createdDate.getMonth() === snapshot.getMonth()
      ) {
        newCount += 1;
      }

      const storedBookingDates = (customer.booking_created_at ?? [])
        .map((value) => validTimestamp(value))
        .filter((value): value is number => value !== null);
      const knownBookingDates = storedBookingDates.length > 0
        ? storedBookingDates
        : customer.contact_id
        ? bookingEventsByContact.get(customer.contact_id) ?? []
        : [];
      const knownBookingsAtSnapshot = new Set(
        knownBookingDates.filter((timestamp) => timestamp <= snapshotTimestamp),
      ).size;
      const bookingCount = snapshotIndex === snapshots.length - 1
        ? Math.max(knownBookingsAtSnapshot, customer.total_bookings)
        : knownBookingsAtSnapshot;
      if (bookingCount >= 1) bookedCount += 1;
      if (bookingCount >= 2) repeatCount += 1;
    }

    monthlyActive.push(activeCount);
    inactive.push(Math.max(eligibleCount - activeCount, 0));
    newThisMonth.push(newCount);
    repeatRate.push(bookedCount === 0 ? 0 : (repeatCount / bookedCount) * 100);
  });

  return {
    monthlyActive: { points: monthlyActive, growth: growthBetweenLastPoints(monthlyActive) },
    inactive: { points: inactive, growth: growthBetweenLastPoints(inactive) },
    newThisMonth: { points: newThisMonth, growth: growthBetweenLastPoints(newThisMonth) },
    repeatRate: { points: repeatRate, growth: growthBetweenLastPoints(repeatRate) },
  };
}
